import { useState, useEffect, useCallback, useMemo } from 'react';
import { formatDate, parseDate } from '../../utils/dateUtils';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import HeightIcon from '@mui/icons-material/Height';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import ClinicalDataCard from './ClinicalDataCard';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { medHistoryAPI } from '../../services/api';
import PlatformService from '../../services/PlatformService';
const validationSchema = Yup.object({
  ypsos: Yup.string().nullable(),
  baros: Yup.string().nullable(),
  allergies: Yup.string().nullable(),
  epembaseis: Yup.string().nullable(),
  endokrineis: Yup.string().nullable(),
  kardiaaggeia: Yup.string().nullable(),
  anapneustiko: Yup.string().nullable(),
  neurologiko: Yup.string().nullable(),
  ourologiko: Yup.string().nullable(),
  gynaikologiko: Yup.string().nullable(),
  gastrenteriko: Yup.string().nullable(),
  myoskeletiko: Yup.string().nullable(),
  autoanosa: Yup.string().nullable(),
  synithies: Yup.string().nullable(),
  mynotes: Yup.string().nullable(),
  klironomikotita: Yup.string().nullable(),
});

const MedHistoryTab = ({ 
  patientId, 
  patientName,
  age,
  gender,
  dateOfBirth,
  amka,
  phone,
  address,
  diagnosis
}) => {
  const [medHistory, setMedHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('clinicalViewMode') || 'card');

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('clinicalViewMode', newMode);
    }
  };

  const HistoryReportView = ({ entry, patientInfo }) => {
    const { patientName, age, gender, dateOfBirth, amka, phone, address, diagnosis } = patientInfo;
    
    const Section = ({ title, children }) => (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="primary" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, mb: 1, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
          {title}
        </Typography>
        {children}
      </Box>
    );

    const DataItem = ({ label, value, fullWidth = false }) => (
      <Grid item xs={12} sm={fullWidth ? 12 : 6}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
          {value || '—'}
        </Typography>
      </Grid>
    );

    return (
      <Box sx={{ p: 1 }}>
        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={2}>
            <DataItem label="Patient Name" value={patientName} />
            <DataItem label="Record Date" value={formatDate(entry.created_at)} />
            <DataItem label="Age / Gender" value={`${age || '—'} / ${gender || '—'}`} />
            <DataItem label="Date of Birth" value={dateOfBirth} />
            <DataItem label="AMKA / ID" value={amka} />
            <DataItem label="Phone" value={phone} />
            <DataItem label="Address" value={address} fullWidth />
            <DataItem label="Primary Diagnosis" value={diagnosis} fullWidth />
          </Grid>
        </Box>

        <Section title="Physical Measurements">
          <Grid container spacing={2}>
            <DataItem label="Height (cm/in)" value={entry.ypsos} />
            <DataItem label="Weight (kg/lb)" value={entry.baros} />
          </Grid>
        </Section>

        <Section title="Critical Information">
          <Grid container spacing={2}>
            <DataItem label="Allergies" value={entry.allergies || 'NONE REPORTED'} fullWidth />
            <DataItem label="Previous Surgeries" value={entry.epembaseis} fullWidth />
          </Grid>
        </Section>

        <Section title="Review of Systems">
          <Grid container spacing={2}>
            <DataItem label="Cardiovascular" value={entry.kardiaaggeia} />
            <DataItem label="Respiratory" value={entry.anapneustiko} />
            <DataItem label="Neurological" value={entry.neurologiko} />
            <DataItem label="Urology" value={entry.ourologiko} />
            <DataItem label="Gastroenterology" value={entry.gastrenteriko} />
            <DataItem label="Endocrine / Metabolism" value={entry.endokrineis} />
            <DataItem label="Musculoskeletal" value={entry.myoskeletiko} />
            <DataItem label="Gynecology" value={entry.gynaikologiko} />
            <DataItem label="Autoimmune" value={entry.autoanosa} />
            <DataItem label="Heredity / Family History" value={entry.klironomikotita} />
          </Grid>
        </Section>

        <Section title="Lifestyle & Notes">
          <Grid container spacing={2}>
            <DataItem label="Habits (Smoking, Alcohol, etc.)" value={entry.synithies} fullWidth />
            <DataItem label="General Notes" value={entry.mynotes} fullWidth />
          </Grid>
        </Section>
      </Box>
    );
  };

  const loadMedHistory = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    setError('');

    try {
      const response = await medHistoryAPI.getByPatientId(patientId);
      setMedHistory(response.data);
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load medical history');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadMedHistory();
  }, [loadMedHistory]);

  const handleAdd = () => {
    setEditingEntry(null);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handlePreview = (entry) => {
    setEditingEntry(entry);
    setViewOnly(true);
    setDialogOpen(true);
  };

  const handlePrint = (entry) => {
    const reportDate = formatDate(new Date());
    const entryDate = formatDate(entry.created_at);

    const reportHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Medical History - ${patientName}</title>
        <style>
          body { font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
          .header { border-bottom: 2px solid #1976d2; margin-bottom: 30px; padding-bottom: 10px; }
          .title { font-size: 24px; font-weight: bold; color: #1976d2; text-transform: uppercase; }
          .subtitle { font-size: 14px; color: #666; margin-top: 5px; }
          .metadata { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; background: #f8f9fa; padding: 15px; border-radius: 8px; }
          .metadata-label { font-weight: bold; color: #555; font-size: 12px; text-transform: uppercase; }
          .metadata-value { font-size: 16px; margin-top: 2px; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 18px; font-weight: bold; color: #1976d2; border-bottom: 1px solid #e0e0e0; margin-bottom: 10px; padding-bottom: 5px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .item-label { font-weight: bold; color: #777; font-size: 11px; text-transform: uppercase; }
          .item-value { font-size: 14px; white-space: pre-wrap; }
          .full-width { grid-column: span 2; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">Medical History Report</div>
          <div class="subtitle">Generated on ${reportDate}</div>
        </div>
        
        <div class="metadata">
          <div>
            <div class="metadata-label">Patient Name</div>
            <div class="metadata-value">${patientName || 'N/A'}</div>
          </div>
          <div>
            <div class="metadata-label">Record Date</div>
            <div class="metadata-value">${entryDate}</div>
          </div>
          <div>
            <div class="metadata-label">Age / Gender</div>
            <div class="metadata-value">${age || '—'} / ${gender || '—'}</div>
          </div>
          <div>
            <div class="metadata-label">Date of Birth</div>
            <div class="metadata-value">${dateOfBirth || '—'}</div>
          </div>
          <div>
            <div class="metadata-label">AMKA / ID</div>
            <div class="metadata-value">${amka || '—'}</div>
          </div>
          <div>
            <div class="metadata-label">Phone</div>
            <div class="metadata-value">${phone || '—'}</div>
          </div>
          <div class="full-width">
            <div class="metadata-label">Primary Diagnosis</div>
            <div class="metadata-value">${diagnosis || 'NONE RECORDED'}</div>
          </div>
          <div class="full-width">
            <div class="metadata-label">Address</div>
            <div class="metadata-value">${address || '—'}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Physical Measurements</div>
          <div class="grid">
            <div>
              <div class="item-label">Height</div>
              <div class="item-value">${entry.ypsos || '—'}</div>
            </div>
            <div>
              <div class="item-label">Weight</div>
              <div class="item-value">${entry.baros || '—'}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Critical Information</div>
          <div class="grid">
            <div class="full-width">
              <div class="item-label">Allergies</div>
              <div class="item-value">${entry.allergies || 'NONE REPORTED'}</div>
            </div>
            <div class="full-width" style="margin-top: 5px;">
              <div class="item-label">Previous Surgeries</div>
              <div class="item-value">${entry.epembaseis || '—'}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Review of Systems</div>
          <div class="grid">
            <div>
              <div class="item-label">Cardiovascular</div>
              <div class="item-value">${entry.kardiaaggeia || '—'}</div>
            </div>
            <div>
              <div class="item-label">Respiratory</div>
              <div class="item-value">${entry.anapneustiko || '—'}</div>
            </div>
            <div>
              <div class="item-label">Neurological</div>
              <div class="item-value">${entry.neurologiko || '—'}</div>
            </div>
            <div>
              <div class="item-label">Urology</div>
              <div class="item-value">${entry.ourologiko || '—'}</div>
            </div>
            <div>
              <div class="item-label">Gastroenterology</div>
              <div class="item-value">${entry.gastrenteriko || '—'}</div>
            </div>
            <div>
              <div class="item-label">Endocrine / Metabolism</div>
              <div class="item-value">${entry.endokrineis || '—'}</div>
            </div>
            <div>
              <div class="item-label">Musculoskeletal</div>
              <div class="item-value">${entry.myoskeletiko || '—'}</div>
            </div>
            <div>
              <div class="item-label">Gynecology</div>
              <div class="item-value">${entry.gynaikologiko || '—'}</div>
            </div>
            <div>
              <div class="item-label">Autoimmune</div>
              <div class="item-value">${entry.autoanosa || '—'}</div>
            </div>
            <div>
              <div class="item-label">Heredity / Family History</div>
              <div class="item-value">${entry.klironomikotita || '—'}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Lifestyle & Notes</div>
          <div class="grid">
            <div class="full-width">
              <div class="item-label">Habits (Smoking, Alcohol, etc.)</div>
              <div class="item-value">${entry.synithies || '—'}</div>
            </div>
            <div class="full-width" style="margin-top: 10px;">
              <div class="item-label">General Notes</div>
              <div class="item-value" style="background: #fdfdfd; padding: 10px; border: 1px solid #efefef;">${entry.mynotes || '—'}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    const patientFileName = `${patientName || 'Patient'}_MedicalHistory_${entryDate.replace(/\//g, '-')}`;
    
    // Use the unified PlatformService for high-fidelity clinical printing
    PlatformService.print(reportHtml, patientFileName);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this medical history entry?')) return;

    try {
      await medHistoryAPI.delete(id);
      setMedHistory((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete medical history');
    }
  };

  const handleSubmit = async (values, { setStatus }) => {
    setSubmitting(true);
    setStatus(undefined);

    try {
      const payload = {
        id_pt: patientId,
        ...values,
      };

      if (editingEntry) {
        const response = await medHistoryAPI.update(editingEntry.id, payload);
        const updated = response.data;
        setMedHistory((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      } else {
        const response = await medHistoryAPI.create(payload);
        const created = response.data;
        setMedHistory((prev) => [created, ...prev]);
      }

      setDialogOpen(false);
    } catch (err) {
      setStatus(err.response?.data?.error ?? err.message ?? 'Failed to save medical history');
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    ypsos: editingEntry?.ypsos ?? '',
    baros: editingEntry?.baros ?? '',
    allergies: editingEntry?.allergies ?? '',
    epembaseis: editingEntry?.epembaseis ?? '',
    endokrineis: editingEntry?.endokrineis ?? '',
    kardiaaggeia: editingEntry?.kardiaaggeia ?? '',
    anapneustiko: editingEntry?.anapneustiko ?? '',
    neurologiko: editingEntry?.neurologiko ?? '',
    ourologiko: editingEntry?.ourologiko ?? '',
    gynaikologiko: editingEntry?.gynaikologiko ?? '',
    gastrenteriko: editingEntry?.gastrenteriko ?? '',
    myoskeletiko: editingEntry?.myoskeletiko ?? '',
    autoanosa: editingEntry?.autoanosa ?? '',
    synithies: editingEntry?.synithies ?? '',
    mynotes: editingEntry?.mynotes ?? '',
    klironomikotita: editingEntry?.klironomikotita ?? '',
  };

  const sortedMedHistory = useMemo(() => {
    return [...medHistory].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'created_at') {
        aValue = parseDate(aValue)?.getTime() || 0;
        bValue = parseDate(bValue)?.getTime() || 0;
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [medHistory, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Medical History</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              value={orderBy}
              label="Sort By"
              onChange={(e) => setOrderBy(e.target.value)}
            >
              <MenuItem value="created_at">Date</MenuItem>
              <MenuItem value="allergies">Allergies</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={`Sort ${order === 'asc' ? 'Descending' : 'Ascending'}`}>
            <IconButton onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')} color="primary">
              <TableSortLabel active direction={order} hideSortIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadMedHistory} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            color="primary"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            New Entry
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {medHistory.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No medical history records found.
          </Typography>
        </Paper>
      ) : viewMode === 'card' ? (
        <Grid container spacing={2}>
          {sortedMedHistory.map((entry) => (
            <Grid item xs={12} sm={6} md={4} key={entry.id}>
              <ClinicalDataCard
                title={entry.allergies || 'Medical History Entry'}
                date={formatDate(entry.created_at)}
                type="history"
                content={[
                  { 
                    label: 'Vitals', 
                    value: (entry.ypsos || entry.baros) 
                      ? `${entry.ypsos ? `Height: ${entry.ypsos} cm` : ''}${entry.ypsos && entry.baros ? ' | ' : ''}${entry.baros ? `Weight: ${entry.baros} kg` : ''}` 
                      : null 
                  },
                  { label: 'CV / Resp', value: entry.kardiaaggeia || entry.anapneustiko ? `${entry.kardiaaggeia || '—'} / ${entry.anapneustiko || '—'}` : null },
                  { label: 'Notes', value: entry.mynotes },
                ]}
                onEdit={() => handleEdit(entry)}
                onDelete={() => handleDelete(entry.id)}
                onPreview={() => handlePreview(entry)}
                onPrint={() => handlePrint(entry)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', py: 1.5 }}>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    DATE
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>
                  <TableSortLabel
                    active={orderBy === 'allergies'}
                    direction={orderBy === 'allergies' ? order : 'asc'}
                    onClick={() => handleRequestSort('allergies')}
                  >
                    ALLERGIES
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>HEIGHT / WEIGHT</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>CV / RESP</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', textAlign: 'center' }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMedHistory.map((entry) => (
                <TableRow key={entry.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatDate(entry.created_at)}
                  </TableCell>
                  <TableCell>{entry.allergies || 'NONE'}</TableCell>
                  <TableCell>
                    {entry.ypsos ? `${entry.ypsos} cm` : ''}
                    {entry.ypsos && entry.baros ? ' / ' : ''}
                    {entry.baros ? `${entry.baros} kg` : ''}
                  </TableCell>
                  <TableCell>
                    {entry.kardiaaggeia || entry.anapneustiko ? `${entry.kardiaaggeia || '—'} / ${entry.anapneustiko || '—'}` : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => handlePreview(entry)} color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Print">
                        <IconButton size="small" onClick={() => handlePrint(entry)} color="info">
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(entry)} color="secondary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(entry.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewOnly ? "Medical History Preview" : editingEntry ? 'Edit Medical History' : 'New Medical History Entry'}
        </DialogTitle>
        {viewOnly ? (
          <>
            <DialogContent dividers>
              <HistoryReportView 
                entry={editingEntry} 
                patientInfo={{ patientName, age, gender, dateOfBirth, amka, phone, address, diagnosis }} 
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setDialogOpen(false)} color="inherit">
                Close
              </Button>
              {editingEntry && (
                <Button 
                  onClick={() => handlePrint(editingEntry)} 
                  color="info" 
                  startIcon={<PrintIcon />}
                  sx={{ mr: 'auto', ml: 1 }}
                >
                  Print
                </Button>
              )}
            </DialogActions>
          </>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, touched, errors, handleBlur, status }) => (
              <Form>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="ypsos"
                        label="Height (cm/in)"
                        value={values.ypsos}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="baros"
                        label="Weight (kg/lb)"
                        value={values.baros}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="allergies"
                        label="Allergies"
                        value={values.allergies}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="epembaseis"
                        label="Previous Surgeries"
                        value={values.epembaseis}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'primary.main' }}>
                        Review of Systems
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="kardiaaggeia"
                        label="Cardiovascular"
                        value={values.kardiaaggeia}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="anapneustiko"
                        label="Respiratory"
                        value={values.anapneustiko}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="neurologiko"
                        label="Neurological"
                        value={values.neurologiko}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="ourologiko"
                        label="Urology"
                        value={values.ourologiko}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="gastrenteriko"
                        label="Gastroenterology"
                        value={values.gastrenteriko}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="endokrineis"
                        label="Endocrine / Metabolism"
                        value={values.endokrineis}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="myoskeletiko"
                        label="Musculoskeletal"
                        value={values.myoskeletiko}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="gynaikologiko"
                        label="Gynecology"
                        value={values.gynaikologiko}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="autoanosa"
                        label="Autoimmune"
                        value={values.autoanosa}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="klironomikotita"
                        label="Heredity / Family History"
                        value={values.klironomikotita}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="synithies"
                        label="Habits (Smoking, Alcohol, etc.)"
                        value={values.synithies}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="mynotes"
                        label="General Notes"
                        value={values.mynotes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                        minRows={3}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                  <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                    {submitting ? 'Sharing...' : 'Save Record'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </Box>
  );
};

export default MedHistoryTab;
